import { useState, useRef, useEffect, useCallback } from 'react';
import { HiX, HiChevronDown } from 'react-icons/hi';

export interface MultiSelectOption {
  id: number;
  label: string;
}

interface SearchableMultiSelectProps {
  options: MultiSelectOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  label?: string;
  loading?: boolean;
}

export const SearchableMultiSelect = ({
  options,
  selectedIds,
  onChange,
  placeholder = 'Search and select...',
  label,
  loading = false,
}: SearchableMultiSelectProps) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(
    (opt) =>
      !selectedIds.includes(opt.id) &&
      opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedOptions = options.filter((opt) => selectedIds.includes(opt.id));

  const handleSelect = useCallback(
    (id: number) => {
      onChange([...selectedIds, id]);
      setSearch('');
      inputRef.current?.focus();
    },
    [selectedIds, onChange]
  );

  const handleRemove = useCallback(
    (id: number) => {
      onChange(selectedIds.filter((sid) => sid !== id));
    },
    [selectedIds, onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
    if (e.key === 'Backspace' && search === '' && selectedIds.length > 0) {
      handleRemove(selectedIds[selectedIds.length - 1]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block mb-1 text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {/* Selected chips */}
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedOptions.map((opt) => (
            <span
              key={opt.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded"
            >
              {opt.label}
              <button
                type="button"
                onClick={() => handleRemove(opt.id)}
                className="text-blue-600 hover:text-blue-800"
                aria-label={`Remove ${opt.label}`}
              >
                <HiX className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedIds.length > 0 ? 'Add more...' : placeholder}
          className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-8 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
          disabled={loading}
        />
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            inputRef.current?.focus();
          }}
          className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400"
          tabIndex={-1}
        >
          <HiChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {search ? 'No matches found' : 'No options available'}
            </div>
          ) : (
            filteredOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleSelect(opt.id)}
                className="block w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
